import { CommonModule } from '@angular/common'
import { Component, effect, input, OnDestroy, output, signal, ViewChild } from '@angular/core'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { DatePickerComponent } from '@app/v2/shared/components/inputs/date-picker/date-picker.component'
import { SelectInputComponent } from '@app/v2/shared/components/inputs/select-input/select-input.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { TranslateModule } from '@ngx-translate/core'
import { PaymentType } from '@workspaces/models/payment-type'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { Subject, takeUntil } from 'rxjs'
import { OrganizationService } from '../../services/organization.service'
import { MicetribeAdmin } from '../../types/organization-settings.type'
import { EventCardComponent } from '../event-card/event-card.component'

@Component({
  selector: 'app-micetribe-admin',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DatePickerComponent,
    SelectInputComponent,
    NgxSkeletonLoaderModule,
    ModalComponent,
    EventCardComponent,
    TranslateModule
  ],
  templateUrl: './micetribe-admin.component.html',
  styleUrl: './micetribe-admin.component.scss'
})
export class MicetribeAdminComponent implements OnDestroy {
  public isLoading = input<boolean>(true)
  public micetribeAdminData = input<MicetribeAdmin | null>(null)
  public availableGateways = input<PaymentType[]>([])
  public updateRequested = output<void>()
  @ViewChild('paymentGatewaySelect') paymentGatewaySelect!: SelectInputComponent

  public proForm = signal<FormGroup | null>(null)
  public paymentGatewayOptions = signal<PaymentType[]>([])
  public paidEvents: any[] = []
  public minDate: Date = new Date()
  public isModalOpen = signal<boolean>(false)
  public modalId = signal<string>('paid-events-warning')
  private _pendingPaymentGatewayValue: any = null
  private _originalPaymentGatewayValue: any = null
  private _destroy$ = new Subject<void>()
  // Track if component has been fully initialized
  private _componentReady = false

  constructor(private _organizationService: OrganizationService, private _authService: AuthService, private _modalService: ModalService, private _router: Router) {
    this._initializeProForm()
    effect(() => {
      if (this.micetribeAdminData()) if (!this.isLoading() && this.micetribeAdminData()) this._getProForm()
      if (this.availableGateways()) this.paymentGatewayOptions.set(this.availableGateways())
      if (this._modalService.isModalOpen(this.modalId()) !== this.isModalOpen()) this._modalService.toggleModal(this.modalId())
    })
  }
  ngOnDestroy(): void {
    this._destroy$.next()
    this._destroy$.complete()
  }

  /**
   * @description Initializes the pro form
   */
  private _initializeProForm(): void {
    this.proForm.set(
      new FormGroup({
        pro: new FormControl(false),
        planExpiryDate: new FormControl(''),
        paymentGateway: new FormControl('')
      })
    )
  }

  /**
   * @description Gets the pro form
   */
  private _getProForm(): void {
    this.proForm().controls.pro.setValue(this.micetribeAdminData()?.pro)

    const expiryDate = this.micetribeAdminData()?.plan_expiry_date

    if (expiryDate && expiryDate.trim() !== '' && expiryDate !== 'null') {
      // Convert ISO date string to YYYY-MM-DD format
      const formattedDate = expiryDate.split('T')[0]
      this.proForm().controls.planExpiryDate.setValue(formattedDate)
    } else this.proForm().controls.planExpiryDate.setValue('')

    // Set payment gateway from the workspace options
    const paymentGateway = this.micetribeAdminData()?.payments?.[0]?.gateway

    if (paymentGateway && paymentGateway.trim() !== '') this.proForm().controls.paymentGateway.setValue(paymentGateway)
    else this.proForm().controls.paymentGateway.setValue(null)

    this.proForm().markAsPristine()

    setTimeout(() => (this._componentReady = true))
  }

  /**
   * @description On payment gateway changed
   * @param {any} value Value
   */
  public onPaymentGatewayChanged(value: any): void {
    if (!this._componentReady) return

    const newValue = value === null || value === undefined ? null : value === '' ? null : value
    const currentValue = this.proForm().controls.paymentGateway.value

    // If the value hasn't actually changed, don't proceed
    if (newValue === currentValue) return

    // Check for paid events before allowing the change
    this._checkPaidEventsBeforeUpdate(newValue, currentValue)
  }

  /**
   * On blur event
   * @param {FocusEvent} event Event
   */
  public onBlur(event: FocusEvent): void {
    if (this.isFormDirty()) this.updateRequested.emit()
  }

  /**
   * On child keydown
   * @param {KeyboardEvent} event Event
   */
  public onChildKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      if (this.isFormDirty()) this.updateRequested.emit()
    }
  }

  /**
   * @description On pro toggle change
   */
  public onProToggleChange(): void {
    if (this.isFormDirty()) this.updateRequested.emit()
  }

  /**
   * @description On date change
   * @param {string} value New date value
   */
  public onDateChange(value: string): void {
    if (this.isFormDirty()) this.updateRequested.emit()
  }

  /**
   * @description Check if form has unsaved changes
   * @returns {boolean} True if form has unsaved changes, false otherwise
   */
  public isFormDirty(): boolean {
    return this._organizationService.isFormDirty(this.proForm()!)
  }

  /**
   * Check for paid events before updating payment gateway
   * @param newValue New payment gateway value
   * @param currentValue Current payment gateway value
   */
  private _checkPaidEventsBeforeUpdate(newValue: any, currentValue: any): void {
    const workspaceId = this._authService.currentWorkspace?.id
    if (!workspaceId) return

    this._organizationService
      .getWorkspacePaidEvents(workspaceId.toString())
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (paidEventsData) => {
          // Check if there are paid events
          if (paidEventsData && Array.isArray(paidEventsData) && paidEventsData.length > 0) {
            // Store values for modal
            this._originalPaymentGatewayValue = currentValue
            this._pendingPaymentGatewayValue = newValue
            this.paidEvents = paidEventsData
            // Show the paid events warning modal
            this.isModalOpen.set(true)
          } else {
            // No paid events, confirm the pending value
            this.paymentGatewaySelect.confirmPendingValue()
            this._updatePaymentGateway(newValue)
          }
        },
        error: (error) => {
          console.error('Error checking paid events:', error)
          // Proceed with update even if check fails - confirm the pending value
          this.paymentGatewaySelect.confirmPendingValue()
          this._updatePaymentGateway(newValue)
        }
      })
  }

  /**
   * Update the payment gateway after paid events check
   * @param newValue New payment gateway value
   */
  private _updatePaymentGateway(newValue: any): void {
    // Clear server errors when a valid value is selected
    if (newValue && this.proForm().controls.paymentGateway.errors?.['serverError']) {
      this._organizationService.handleServerValidationErrors(this.proForm(), null)
    }

    this.proForm().controls.paymentGateway.markAsDirty()
    this.proForm().markAsDirty()
    this.updateRequested.emit()
  }

  /**
   * Close paid events modal
   */
  public closePaidEventsModal(): void {
    this.isModalOpen.set(false)
    // Cancel the pending value to reset the UI
    this.paymentGatewaySelect.cancelPendingValue()
    // Clear stored values
    this._originalPaymentGatewayValue = null
    this._pendingPaymentGatewayValue = null
    this.paidEvents = []
  }

  /**
   * Confirm payment gateway change despite paid events
   */
  public confirmPaymentGatewayChange(): void {
    if (this._pendingPaymentGatewayValue !== null) {
      // Confirm the pending value in the select-input component
      this.paymentGatewaySelect.confirmPendingValue()
      this._updatePaymentGateway(this._pendingPaymentGatewayValue)
      console.log('Payment gateway updated to:', this._pendingPaymentGatewayValue)
      this._originalPaymentGatewayValue = null
      this._pendingPaymentGatewayValue = null
    }
    this.isModalOpen.set(false)
    this.paidEvents = []
  }

  /**
   * Handle event selection from event cards - navigate to event wizard
   */
  public onEventSelected(event: any): void {
    this.isModalOpen.set(false)
    this._router.navigate([`/wizard/update/${event.id}`])
  }

  /**
   * @description Gets skeleton theme colors based on current theme
   * @returns {object} Skeleton theme colors
   */
  public getSkeletonTheme(): { backgroundColor: string; color: string } {
    return this._organizationService.getSkeletonTheme()
  }
}
